import CountUp from 'react-countup';
import WidgetShell from '../WidgetShell/WidgetShell';
import WidgetLink from '../WidgetLink/WidgetLink';
import WidgetProgressRow from '../WidgetProgressRow/WidgetProgressRow';
import {
  REMINDERS_PAGE_SIZE,
  REMINDER_FREQUENCIES,
  REMINDER_FREQUENCIES_LABELS,
  REMINDER_STATUSES,
  REMINDER_STATUSES_LABELS,
  REMINDER_TYPES,
  REMINDER_TYPES_LABELS,
} from 'constants/reminders';
import {
  REMINDER_FREQUENCY_COLORS,
  REMINDER_STATUS_COLORS,
  REMINDER_TYPE_COLORS,
} from 'constants/dashboard';

const STATUS_ORDER = [
  REMINDER_STATUSES.ACTIVE,
  REMINDER_STATUSES.PAUSED,
  REMINDER_STATUSES.COMPLETED,
  REMINDER_STATUSES.FAILED,
];

const TYPE_ORDER = [REMINDER_TYPES.ONE_TIME, REMINDER_TYPES.REPEATING];

const FREQUENCY_ORDER = [
  REMINDER_FREQUENCIES.DAILY,
  REMINDER_FREQUENCIES.WEEKLY,
  REMINDER_FREQUENCIES.MONTHLY,
  REMINDER_FREQUENCIES.ANNUAL,
];

const ROW_ANIMATION_DELAY_MS = 60;

const buildReminderLink = (filters = {}) => {
  const params = new URLSearchParams({
    page: '1',
    limit: String(REMINDERS_PAGE_SIZE),
  });

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    }
  });

  return `/reminders?${params.toString()}`;
};

function RemindersWidget({ data, typeData, frequencyData, loading, error, onRetry }) {
  const hasStatusData = Boolean(data && Object.keys(data).length > 0);
  const hasTypeData = Boolean(typeData && Object.keys(typeData).length > 0);
  const hasFrequencyData = Boolean(frequencyData && Object.keys(frequencyData).length > 0);
  const statusTotal = hasStatusData
    ? STATUS_ORDER.reduce((sum, status) => sum + (data?.[status] || 0), 0)
    : 0;
  const typeTotal = hasTypeData
    ? TYPE_ORDER.reduce((sum, type) => sum + (typeData?.[type] || 0), 0)
    : 0;
  const frequencyTotal = hasFrequencyData
    ? FREQUENCY_ORDER.reduce((sum, frequency) => sum + (frequencyData?.[frequency] || 0), 0)
    : 0;
  const total = statusTotal || typeTotal || frequencyTotal;
  const hasData = hasStatusData || hasTypeData || hasFrequencyData;

  return (
    <WidgetShell
      title={
        <WidgetLink
          to={buildReminderLink()}
        >
          Reminders
        </WidgetLink>
      }
      actions={
        !loading && !error && hasData ? (
          <span className="text-3xl font-bold leading-none text-white tabular-nums">
            <CountUp end={total} duration={1.2} preserveValue />
          </span>
        ) : null
      }
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={!hasData}
      emptyMessage="No reminders yet"
    >
      <div className="space-y-4">
        <div className="space-y-3">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</h4>
          {STATUS_ORDER.map((status, index) => (
            <WidgetProgressRow
              key={status}
              label={REMINDER_STATUSES_LABELS[status]}
              count={data?.[status] || 0}
              total={statusTotal}
              colorClass={REMINDER_STATUS_COLORS[status]?.dot || REMINDER_STATUS_COLORS.ACTIVE.dot}
              animationDelay={index * ROW_ANIMATION_DELAY_MS}
              to={buildReminderLink({ status })}
            />
          ))}
        </div>

        {hasTypeData && (
          <div className="space-y-3">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Type</h4>
            {TYPE_ORDER.map((type, index) => {
              const count = typeData?.[type] || 0;
              if (count === 0 && !typeData?.[type]) return null;

              return (
                <WidgetProgressRow
                  key={type}
                  label={REMINDER_TYPES_LABELS[type]}
                  count={count}
                  total={typeTotal}
                  colorClass={REMINDER_TYPE_COLORS[type]?.bar || 'bg-gray-500'}
                  showDot={false}
                  animationDelay={(STATUS_ORDER.length + index) * ROW_ANIMATION_DELAY_MS}
                  to={buildReminderLink({ type })}
                />
              );
            })}
          </div>
        )}

        {hasFrequencyData && (
          <div className="space-y-3">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</h4>
            {FREQUENCY_ORDER.map((frequency, index) => {
              const count = frequencyData?.[frequency] || 0;
              if (count === 0 && !frequencyData?.[frequency]) return null;

              return (
                <WidgetProgressRow
                  key={frequency}
                  label={REMINDER_FREQUENCIES_LABELS[frequency]}
                  count={count}
                  total={frequencyTotal}
                  colorClass={REMINDER_FREQUENCY_COLORS[frequency]?.bar || 'bg-gray-500'}
                  showDot={false}
                  animationDelay={(STATUS_ORDER.length + TYPE_ORDER.length + index) * ROW_ANIMATION_DELAY_MS}
                />
              );
            })}
          </div>
        )}
      </div>
    </WidgetShell>
  );
}

export default RemindersWidget;
